import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent } from '../ui/ResponsiveLayout';
import BrandSymbol from '../ui/BrandSymbol';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useRole } from '../../contexts/RoleContext';
import { apiService } from '../../utils/api';
import { settingsService, type AccountDeletionStatus } from '../../services/settingsService';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.endofanera.ai';
const ONBOARDING_TUTORIAL_ID = 'onboarding_video';

interface ExecutorEstate {
  estateOwnerId: string;
  ownerName: string;
  ownerEmail: string;
  inviteStatus: string;
  hasApprovedDeathCert: boolean;
  ownerHasAccess: boolean;
  ownerSubscriptionStatus: string;
}

interface BeneficiaryEstate {
  estateOwnerId: string;
  ownerName: string;
  ownerEmail: string;
  inviteStatus: string;
}

const EstateSelector: React.FC = () => {
    // Initial entry role from backend
    const [, setInitialEntryRole] = useState<'planner' | 'executor' | 'beneficiary' | null>(null);

    // Function to order tiles based on initial entry role
    function getOrderedTiles() {
      const executorTiles = executorEstates.map(e => ({ type: 'executorEstate', estate: e }));
      const beneficiaryTiles = beneficiaryEstates.map(e => ({ type: 'beneficiaryEstate', estate: e }));
      return [
        ...executorTiles,
        ...beneficiaryTiles,
        'myEstatePlan'
      ];
    }
  const navigate = useNavigate();
  const { switchRole } = useRole();
  const [loading, setLoading] = useState(true);
  const [executorEstates, setExecutorEstates] = useState<ExecutorEstate[]>([]);
  const [beneficiaryEstates, setBeneficiaryEstates] = useState<BeneficiaryEstate[]>([]);
  const [userName, setUserName] = useState('');
  const [isFirstEntry, setIsFirstEntry] = useState(true);

  const { status, isLoading } = useSubscriptionStatus();

  const [deletionStatus, setDeletionStatus] = useState<AccountDeletionStatus | null>(null);

  useEffect(() => {
    const fetchRolesAndWelcome = async () => {
      // Read from localStorage directly - session.accountToken not available during initial load after MFA
      const token = localStorage.getItem('account-token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/api/user/roles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();

          // Set executor estates
          if (data.executorFor && data.executorFor.length > 0) {
            const seen = new Set();
            const uniqueExecutorList = data.executorFor.filter( (e: ExecutorEstate) => {
              const dupe = seen.has(e.estateOwnerId);
              seen.add(e.estateOwnerId);
              return !dupe;
            });
            setExecutorEstates(uniqueExecutorList);
          }
          if (data.beneficiaryFor && data.beneficiaryFor.length > 0) {
            const seen = new Set();
            const uniqueBeneficiaryList = data.beneficiaryFor.filter( (e: BeneficiaryEstate) => {
              const dupe = seen.has(e.estateOwnerId);
              seen.add(e.estateOwnerId);
              return !dupe;
            });
            setBeneficiaryEstates(uniqueBeneficiaryList);
          }
          // Determine initial entry role from roles array
          if (data.roles && Array.isArray(data.roles) && data.roles.length > 0) {
            // Map backend role string to local type
            const roleMap: Record<string, 'planner' | 'executor' | 'beneficiary'> = {
              'Planning My Own Estate': 'planner',
              'Executor for Someone Else': 'executor',
              'Beneficiary': 'beneficiary'
            };
            const firstRole = data.roles[0];
            setInitialEntryRole(roleMap[firstRole] || 'planner');

            const deletionStatus = await settingsService.getDeletionStatus();
            if (deletionStatus.success) {
              setDeletionStatus(deletionStatus);
            }
          } else {
            setInitialEntryRole('planner');
          }
          // Get user name from session storage or local storage
          const storedName = sessionStorage.getItem('mfa_name') || localStorage.getItem('user_name') || '';
          setUserName(storedName);

          // Backend-driven welcome logic
          const hasEntered = await apiService.getEstateSelectorWelcomeFlag();
          setIsFirstEntry(!hasEntered);
          if (!hasEntered) {
            await apiService.setEstateSelectorWelcomeFlag();
          }
          setLoading(false);
        } else {
          setLoading(false);
          navigate('/frame-140');
          return;
        }
      } catch (error) {
        console.error('[EstateSelector] Failed to fetch roles:', error);
        setLoading(false);
        navigate('/frame-140');
      }
    };
    fetchRolesAndWelcome();

  }, [navigate]);

  const handleSelectOwnEstate = async () => {
    if (!isLoading && status !== 'active' && status !== 'trialing') {
      navigate('/frame-250');
      return;
    }

    // For multi-role users, onboarding is checked when entering My Estate Plan.
    const token = localStorage.getItem('account-token');
    if (token) {
      try {
        const tutorialResponse = await fetch(
          `${API_BASE_URL}/api/user/tutorial-status/${ONBOARDING_TUTORIAL_ID}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (tutorialResponse.ok) {
          const tutorialData = await tutorialResponse.json();
          if (!tutorialData.dismissed) {
            navigate('/frame-134', { state: { redirectTo: '/frame-140' } });
            return;
          }
        }
      } catch (error) {
        console.error('[EstateSelector] Failed to check tutorial status:', error);
      }
    }

    navigate('/frame-140');
  };

  const handleSelectExecutorEstate = (estate: ExecutorEstate) => {
    switchRole({
      role: 'executor',
      estateOwnerId: estate.estateOwnerId,
      estateOwnerName: estate.ownerName,
      estateOwnerEmail: estate.ownerEmail,
      inviteStatus: estate.inviteStatus,
      hasApprovedDeathCert: estate.hasApprovedDeathCert,
    });
    navigate(`/executor-plan/${estate.estateOwnerId}/eframe-141`, {
      state: { ownerName: estate.ownerName }
    });
  };

  const handleSelectBeneficiaryEstate = (estate: BeneficiaryEstate) => {
    navigate(`/beneficiary-coming-soon/${estate.estateOwnerId}`, {
      state: { ownerName: estate.ownerName }
    });
  };

  if (loading) {
    return (
      <ResponsiveContainer background="stone">
        <ResponsiveContent>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
            <p className="mt-4 text-stone-600">Loading your estates...</p>
          </div>
        </ResponsiveContent>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="max-w-2xl mx-auto py-12">
          {/* Header - Centered Logo */}
          <div className="flex flex-col items-center mb-10">
            <BrandSymbol size="3xl" variant="dark" showText={true} className="mb-8 mx-auto scale-125" />
            <h1 className="font-display text-3xl font-semibold text-stone-800 mb-3 text-center">
              {isFirstEntry ? 'Welcome' : 'Welcome back'}{userName ? `, ${userName.split(' ')[0]}` : ''}!
            </h1>
            <p className="text-stone-600 text-lg text-center">
              Which estate would you like to work on today?
            </p>
          </div>

          {/* Estate Cards */}
          <div className="space-y-4">
            {/* Estate Tiles - Ordered */}
            {getOrderedTiles().map((tile) => {
              if (tile === 'myEstatePlan' && deletionStatus?.hasPlannerProfile !== false) {
                return (
                  <button
                    key="myEstatePlan"
                    onClick={handleSelectOwnEstate}
                    className="w-full bg-white rounded-xl shadow-sm border-2 border-stone-200 hover:border-cyan-500 hover:shadow-md transition-all p-6 text-left group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center group-hover:bg-cyan-200 transition-colors">
                        <svg className="w-7 h-7 text-cyan-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-stone-800 group-hover:text-cyan-700 transition-colors">
                          My Estate Plan
                        </h2>
                        <p className="text-stone-500 text-sm mt-1">
                          {isFirstEntry ? 'Start planning your own legacy' : 'Continue planning your legacy'}
                        </p>
                      </div>
                      <svg className="w-6 h-6 text-stone-400 group-hover:text-cyan-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                );
              }
              if (typeof tile === 'object' && tile.type === 'executorEstate') {
                const estate = tile.estate;

                if (!estate.ownerHasAccess) {
                  return (
                    <div key={estate.estateOwnerId} className="w-full bg-white rounded-xl shadow-sm border-2 border-stone-200 p-6 text-left">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                          <svg className="w-7 h-7 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h2 className="text-xl font-semibold text-stone-800">
                            {estate.ownerName}'s Estate
                          </h2>
                          <p className="text-stone-500 text-sm mt-1">
                          This estate plan is currently inactive because the planner's subscription is not active. Access will be restored when the subscription is reactivated.
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                }
                return (
                  <button
                    key={estate.estateOwnerId}
                    onClick={() => handleSelectExecutorEstate(estate)}
                    className="w-full bg-white rounded-xl shadow-sm border-2 border-stone-200 hover:border-amber-500 hover:shadow-md transition-all p-6 text-left group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                        <svg className="w-7 h-7 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-stone-800 group-hover:text-amber-700 transition-colors">
                          {estate.ownerName}'s Estate
                        </h2>
                        <p className="text-stone-500 text-sm mt-1">
                          You are designated as an executor
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                          Executor
                        </span>
                        <svg className="w-6 h-6 text-stone-400 group-hover:text-amber-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                );
              }
              if (typeof tile === 'object' && tile.type === 'beneficiaryEstate') {
                const estate = tile.estate;
                return (
                  <button
                    key={`beneficiary-${estate.estateOwnerId}`}
                    onClick={() => handleSelectBeneficiaryEstate(estate)}
                    className="w-full bg-white rounded-xl shadow-sm border-2 border-stone-200 hover:border-purple-500 hover:shadow-md transition-all p-6 text-left group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                        <svg className="w-7 h-7 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 01-8 0M12 14v7m-7-3a7 7 0 0114 0" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h2 className="text-xl font-semibold text-stone-800 group-hover:text-purple-700 transition-colors">
                          {estate.ownerName}'s Estate
                        </h2>
                        <p className="text-stone-500 text-sm mt-1">
                          You have been named a beneficiary for this estate
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                          Beneficiary
                        </span>
                        <svg className="w-6 h-6 text-stone-400 group-hover:text-purple-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                );
              }
              return null;
            })}
          </div>

          {/* Footer note */}
          {/* Removed Settings mention from footer note */}
        </div>
      </ResponsiveContent>
    </ResponsiveContainer>
  );
};

export default EstateSelector;

